import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';

const CART_STORAGE_KEY = 'hamza_cart';

function loadCart() {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch (e) {
    console.warn('Failed to load cart from storage', e);
  }
  return [];
}

function saveCart(items) {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    console.warn('Failed to save cart', e);
  }
}

/** مفتاح فريد لسطر السلة: نفس القطعة لموديلين = سطران منفصلان */
export function cartLineKey(line) {
  const m = line.modelName || line.model_name || '';
  const v = line.variantName || line.variant_name || '';
  return `${line.id}::${m}::${v}`;
}

function normalizeCartItem(sparePart, vehicleCtx = {}) {
  const extra = sparePart.extra_image_urls;
  return {
    id: sparePart.id,
    name_ar: sparePart.name_ar || '',
    name: sparePart.name || '',
    part_number: sparePart.part_number || '',
    price: sparePart.price != null ? sparePart.price : null,
    image_url: sparePart.image_url || null,
    extra_image_urls: Array.isArray(extra) ? extra.filter(Boolean) : [],
    modelName:
      vehicleCtx.modelName ??
      sparePart.model_name ??
      sparePart.modelName ??
      '',
    variantName:
      vehicleCtx.variantName ??
      sparePart.variant_name ??
      sparePart.variantName ??
      '',
    modelSlug: vehicleCtx.modelSlug ?? sparePart.model_slug ?? sparePart.modelSlug ?? '',
    variantSlug: vehicleCtx.variantSlug ?? sparePart.variant_slug ?? sparePart.variantSlug ?? '',
  };
}

function cartReducer(state, action) {
  let nextItems = state.items != null ? state.items : [];
  switch (action.type) {
    case 'ADD': {
      const { item, quantity = 1, vehicleContext = {} } = action.payload;
      const base = normalizeCartItem(item, vehicleContext);
      const line = { ...base, quantity };
      const key = cartLineKey(line);
      const existing = state.items.find((i) => cartLineKey(i) === key);
      if (existing) {
        nextItems = state.items.map((i) =>
          cartLineKey(i) === key ? { ...i, quantity: i.quantity + quantity } : i
        );
      } else {
        nextItems = [...state.items, line];
      }
      break;
    }
    case 'REMOVE': {
      const { id, modelName, variantName } = action.payload;
      const key = `${id}::${modelName || ''}::${variantName || ''}`;
      nextItems = state.items.filter((i) => cartLineKey(i) !== key);
      break;
    }
    case 'UPDATE_QUANTITY': {
      const { id, modelName, variantName, quantity } = action.payload;
      const key = `${id}::${modelName || ''}::${variantName || ''}`;
      if (quantity < 1) {
        nextItems = state.items.filter((i) => cartLineKey(i) !== key);
      } else {
        nextItems = state.items.map((i) =>
          cartLineKey(i) === key ? { ...i, quantity } : i
        );
      }
      break;
    }
    case 'CLEAR':
      nextItems = [];
      break;
    case 'SET_ITEMS':
      nextItems = action.payload.items || [];
      break;
    default:
      return state;
  }
  return { ...state, items: nextItems };
}

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, {
    items: loadCart(),
  });

  useEffect(() => {
    saveCart(state.items);
  }, [state.items]);

  const addToCart = useCallback((sparePart, quantity = 1, vehicleContext = {}) => {
    dispatch({ type: 'ADD', payload: { item: sparePart, quantity, vehicleContext } });
  }, []);

  const removeFromCart = useCallback((line) => {
    dispatch({
      type: 'REMOVE',
      payload: {
        id: line.id,
        modelName: line.modelName || line.model_name,
        variantName: line.variantName || line.variant_name,
      },
    });
  }, []);

  const updateQuantity = useCallback((line, quantity) => {
    dispatch({
      type: 'UPDATE_QUANTITY',
      payload: {
        id: line.id,
        modelName: line.modelName || line.model_name,
        variantName: line.variantName || line.variant_name,
        quantity,
      },
    });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR' });
  }, []);

  const totalCount = (state.items || []).reduce((sum, i) => sum + (i.quantity || 1), 0);

  const value = {
    items: state.items || [],
    totalCount,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
